/**
 * Kickballz multi-product flavor × pack-size picker
 * Maps UI selections to discrete Shopify products (not variants).
 */
if (!customElements.get('kickballz-product-picker')) {
  customElements.define(
    'kickballz-product-picker',
    class KickballzProductPicker extends HTMLElement {
      constructor() {
        super();
        this.products = [];
        this.selectedFlavor = null;
        this.selectedPack = null;
        this.purchaseType = 'otp';
        this.quantity = 1;
        this.activeImageIndex = 0;
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.subscribeDiscountPercent = Number(this.dataset.subscribeDiscount || 15);
      }

      connectedCallback() {
        const dataEl = this.querySelector('[data-picker-json]');
        if (!dataEl) return;

        try {
          this.products = JSON.parse(dataEl.textContent);
        } catch (e) {
          console.error('Kickballz picker: invalid product JSON', e);
          return;
        }

        if (!this.products.length) return;

        this.cacheElements();
        this.bindEvents();
        this.applyInitialSelection();
        this.render();
      }

      applyInitialSelection() {
        const params = new URLSearchParams(window.location.search);
        const flavorParam = params.get('flavor');
        const packParam = params.get('pack');
        const handleHint = this.dataset.initialHandle || '';

        let initial =
          this.products.find((p) => p.handle === handleHint) ||
          this.products.find((p) => p.flavor === flavorParam) ||
          this.products.find((p) => p.available) ||
          this.products[0];

        if (flavorParam && packParam) {
          const matched = this.products.find(
            (p) => p.flavor === flavorParam && p.packSize === packParam
          );
          if (matched) initial = matched;
        } else if (flavorParam) {
          const matched = this.products.find((p) => p.flavor === flavorParam);
          if (matched) initial = matched;
        }

        this.selectedFlavor = initial.flavor;
        this.selectedPack = initial.packSize;
      }

      cacheElements() {
        this.els = {
          title: this.querySelector('[data-kbz-title]'),
          price: this.querySelector('[data-kbz-price]'),
          reviews: this.querySelector('[data-kbz-reviews]'),
          flavors: this.querySelector('[data-kbz-flavors]'),
          packs: this.querySelector('[data-kbz-packs]'),
          otpBtn: this.querySelector('[data-kbz-otp]'),
          subBtn: this.querySelector('[data-kbz-subscribe]'),
          otpPrice: this.querySelector('[data-kbz-otp-price]'),
          subPrice: this.querySelector('[data-kbz-sub-price]'),
          qtyInput: this.querySelector('[data-kbz-qty]'),
          atc: this.querySelector('[data-kbz-atc]'),
          atcLabel: this.querySelector('[data-kbz-atc-label]'),
          form: this.querySelector('[data-kbz-form]'),
          variantInput: this.querySelector('[name="id"]'),
          sellingPlanInput: this.querySelector('[name="selling_plan"]'),
          qtyHidden: this.querySelector('[data-kbz-qty-hidden]'),
          mainImage: this.querySelector('[data-kbz-main-image]'),
          thumbs: this.querySelector('[data-kbz-thumbs]'),
          accordionIngredients: this.querySelector('[data-kbz-ingredients]'),
          accordionWarnings: this.querySelector('[data-kbz-warnings]'),
          error: this.querySelector('[data-kbz-error]'),
          shopPayWrap: this.querySelector('[data-kbz-shop-pay]'),
        };
      }

      bindEvents() {
        this.els.flavors?.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-flavor]');
          if (!btn || btn.disabled) return;
          this.selectedFlavor = btn.dataset.flavor;
          const availablePacks = this.packsForFlavor(this.selectedFlavor);
          if (!availablePacks.includes(this.selectedPack)) {
            this.selectedPack = availablePacks[0];
          }
          this.activeImageIndex = 0;
          this.render();
          this.syncUrlParams();
        });

        this.els.packs?.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-pack]');
          if (!btn || btn.disabled) return;
          this.selectedPack = btn.dataset.pack;
          this.activeImageIndex = 0;
          this.render();
          this.syncUrlParams();
        });

        this.els.otpBtn?.addEventListener('click', () => {
          this.purchaseType = 'otp';
          this.renderPurchaseOptions();
          this.updatePricesAndAtc();
        });

        this.els.subBtn?.addEventListener('click', () => {
          this.purchaseType = 'subscribe';
          this.renderPurchaseOptions();
          this.updatePricesAndAtc();
        });

        this.querySelector('[data-kbz-qty-minus]')?.addEventListener('click', () => {
          this.quantity = Math.max(1, this.quantity - 1);
          this.syncQuantity();
          this.updatePricesAndAtc();
        });

        this.querySelector('[data-kbz-qty-plus]')?.addEventListener('click', () => {
          this.quantity = Math.min(99, this.quantity + 1);
          this.syncQuantity();
          this.updatePricesAndAtc();
        });

        this.els.qtyInput?.addEventListener('change', () => {
          const val = parseInt(this.els.qtyInput.value, 10);
          this.quantity = Number.isFinite(val) && val > 0 ? Math.min(99, val) : 1;
          this.syncQuantity();
          this.updatePricesAndAtc();
        });

        this.els.thumbs?.addEventListener('click', (e) => {
          const thumb = e.target.closest('[data-image-index]');
          if (!thumb) return;
          this.activeImageIndex = Number(thumb.dataset.imageIndex);
          this.renderGallery(this.currentProduct());
        });

        this.els.form?.addEventListener('submit', (e) => this.onSubmit(e));

        this.querySelector('[data-kbz-shop-pay-link]')?.addEventListener('click', (e) => {
          e.preventDefault();
          this.addToCartThenCheckout();
        });
      }

      syncUrlParams() {
        try {
          const url = new URL(window.location.href);
          if (this.selectedFlavor) url.searchParams.set('flavor', this.selectedFlavor);
          if (this.selectedPack) url.searchParams.set('pack', this.selectedPack);
          window.history.replaceState({}, '', url);
        } catch (e) {
          // Ignore history API failures in older browsers / sandboxed previews
        }
      }

      uniqueFlavors() {
        const seen = new Map();
        this.products.forEach((p) => {
          if (!seen.has(p.flavor)) {
            seen.set(p.flavor, p.flavorLabel);
          }
        });
        return [...seen.entries()].map(([flavor, label]) => ({ flavor, label }));
      }

      uniquePacks() {
        const seen = new Map();
        this.products.forEach((p) => {
          if (!seen.has(p.packSize)) {
            seen.set(p.packSize, p.packLabel);
          }
        });
        return [...seen.entries()]
          .map(([packSize, label]) => ({ packSize, label }))
          .sort((a, b) => Number(a.packSize) - Number(b.packSize));
      }

      packsForFlavor(flavor) {
        return this.products
          .filter((p) => p.flavor === flavor)
          .map((p) => p.packSize);
      }

      currentProduct() {
        return (
          this.products.find(
            (p) =>
              p.flavor === this.selectedFlavor &&
              p.packSize === this.selectedPack
          ) || null
        );
      }

      formatMoney(cents) {
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
          return Shopify.formatMoney(cents, this.moneyFormat);
        }
        const amount = (Number(cents) / 100).toFixed(2);
        return this.moneyFormat
          .replace('{{amount_no_decimals}}', String(Math.round(cents / 100)))
          .replace('{{amount}}', amount)
          .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
      }

      displayPrice(product) {
        if (!product) return 0;
        if (this.purchaseType === 'subscribe') {
          if (product.subscribePrice != null) return product.subscribePrice;
          return Math.round(
            product.price * (1 - this.subscribeDiscountPercent / 100)
          );
        }
        return product.price;
      }

      render() {
        const product = this.currentProduct();
        this.renderFlavorButtons();
        this.renderPackButtons();
        this.renderPurchaseOptions();
        this.renderGallery(product);
        this.renderCopy(product);
        this.updateForm(product);
        this.updatePricesAndAtc();
      }

      renderFlavorButtons() {
        if (!this.els.flavors) return;
        const flavors = this.uniqueFlavors();
        this.els.flavors.innerHTML = flavors
          .map(({ flavor, label }) => {
            const isSelected = flavor === this.selectedFlavor;
            const selected = isSelected ? ' is-selected' : '';
            return `<button type="button" class="kbz-pill${selected}" data-flavor="${flavor}" aria-pressed="${isSelected}">${label}</button>`;
          })
          .join('');
      }

      renderPackButtons() {
        if (!this.els.packs) return;
        const packs = this.uniquePacks();
        const available = this.packsForFlavor(this.selectedFlavor);
        this.els.packs.innerHTML = packs
          .map(({ packSize, label }) => {
            const isAvailable = available.includes(packSize);
            const isSelected =
              packSize === this.selectedPack && isAvailable;
            const selected = isSelected ? ' is-selected' : '';
            const disabled = isAvailable ? '' : ' disabled';
            const disabledClass = isAvailable ? '' : ' is-disabled';
            return `<button type="button" class="kbz-pill${selected}${disabledClass}" data-pack="${packSize}" aria-pressed="${isSelected}"${disabled}>${label}</button>`;
          })
          .join('');
      }

      renderPurchaseOptions() {
        this.els.otpBtn?.classList.toggle('is-selected', this.purchaseType === 'otp');
        this.els.subBtn?.classList.toggle(
          'is-selected',
          this.purchaseType === 'subscribe'
        );
      }

      renderGallery(product) {
        if (!product || !this.els.mainImage) return;
        const images = product.images?.length
          ? product.images
          : [{ src: product.featuredImage, alt: product.flavorLabel }];

        const current =
          images[this.activeImageIndex] || images[0] || { src: '', alt: '' };
        this.els.mainImage.src = current.src || '';
        this.els.mainImage.alt = current.alt || product.flavorLabel || '';

        if (this.els.thumbs) {
          this.els.thumbs.innerHTML = images
            .slice(0, 4)
            .map((img, i) => {
              const active = i === this.activeImageIndex ? ' is-active' : '';
              return `<button type="button" class="kbz-gallery__thumb${active}" data-image-index="${i}" aria-label="View image ${i + 1}">
                <img src="${img.src}" alt="${img.alt || ''}" width="120" height="120" loading="lazy">
              </button>`;
            })
            .join('');
        }
      }

      renderCopy(product) {
        if (!product) return;
        if (this.els.title) this.els.title.textContent = product.flavorLabel;
        if (this.els.reviews) {
          const count = product.reviewsCount || 0;
          this.els.reviews.textContent =
            count > 0 ? `(${count} Reviews)` : this.els.reviews.dataset.fallback || '';
        }
        if (this.els.accordionIngredients) {
          this.els.accordionIngredients.innerHTML = product.ingredients
            ? `<p>${product.ingredients}</p>`
            : this.els.accordionIngredients.dataset.default || '';
        }
        if (this.els.accordionWarnings) {
          this.els.accordionWarnings.innerHTML = product.warnings
            ? `<p>${product.warnings}</p>`
            : this.els.accordionWarnings.dataset.default || '';
        }
      }

      updateForm(product) {
        if (!product) return;
        if (this.els.variantInput) {
          this.els.variantInput.value = product.variantId;
          this.els.variantInput.disabled = !product.available;
        }
        if (this.els.form) {
          this.els.form.setAttribute('data-product-handle', product.handle);
        }
        this.syncQuantity();
        this.syncSellingPlan(product);
      }

      syncQuantity() {
        if (this.els.qtyInput) this.els.qtyInput.value = String(this.quantity);
        if (this.els.qtyHidden) this.els.qtyHidden.value = String(this.quantity);
      }

      syncSellingPlan(product) {
        if (!this.els.sellingPlanInput) return;
        if (this.purchaseType === 'subscribe' && product.sellingPlanId) {
          this.els.sellingPlanInput.value = product.sellingPlanId;
          this.els.sellingPlanInput.disabled = false;
        } else {
          this.els.sellingPlanInput.value = '';
          this.els.sellingPlanInput.disabled = true;
        }
      }

      updatePricesAndAtc() {
        const product = this.currentProduct();
        if (!product) return;

        const unit = this.displayPrice(product);
        const otp = product.price;
        const sub =
          product.subscribePrice != null
            ? product.subscribePrice
            : Math.round(product.price * (1 - this.subscribeDiscountPercent / 100));

        if (this.els.price) this.els.price.textContent = this.formatMoney(unit);
        if (this.els.otpPrice) this.els.otpPrice.textContent = this.formatMoney(otp);
        if (this.els.subPrice) this.els.subPrice.textContent = this.formatMoney(sub);

        const lineTotal = unit * this.quantity;
        const available = product.available;
        if (this.els.atc) {
          this.els.atc.disabled = !available;
        }
        if (this.els.atcLabel) {
          this.els.atcLabel.textContent = available
            ? `Add to Cart — ${this.formatMoney(lineTotal)}`
            : 'Sold Out';
        }

        this.syncSellingPlan(product);

        // Hide subscribe option if no selling plan and no discount fallback intended
        if (this.els.subBtn && !product.sellingPlanId && this.dataset.requireSellingPlan === 'true') {
          this.els.subBtn.hidden = true;
          if (this.purchaseType === 'subscribe') {
            this.purchaseType = 'otp';
            this.renderPurchaseOptions();
          }
        }
      }

      buildFormData(product) {
        const formData = new FormData(this.els.form);
        formData.set('id', String(product.variantId));
        formData.set('quantity', String(this.quantity));

        if (this.purchaseType === 'subscribe' && product.sellingPlanId) {
          formData.set('selling_plan', String(product.sellingPlanId));
        } else {
          formData.delete('selling_plan');
        }
        return formData;
      }

      async addToCart(options = {}) {
        const { openDrawer = true, sections = true } = options;
        const product = this.currentProduct();
        if (!product || !product.available) {
          return { ok: false, error: 'unavailable' };
        }

        const formData = this.buildFormData(product);
        const cartDrawer = document.querySelector('drawer-cart');
        const useDrawer = sections && openDrawer && cartDrawer;

        if (useDrawer) {
          formData.append(
            'sections',
            HELPER_UTIL.sectionRenderUtil('cart-drawer').getSectionsString()
          );
          formData.append('sections_url', window.location.pathname);
        }

        const response = await fetch(
          `${window.Shopify?.routes?.root || '/'}cart/add.js`,
          {
            method: 'POST',
            headers: {
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: formData,
          }
        );
        const data = await response.json();

        if (data.status) {
          return {
            ok: false,
            error: data.description || data.message || 'Unable to add to cart',
            data,
          };
        }

        if (useDrawer) {
          window.currentActiveElement = this.els.atc;
          HELPER_UTIL.dispatchCustomEvent(
            _EVENT_HELPER.triggerCartChange,
            this.els.atc || document,
            { sections: data.sections }
          );
        }

        return { ok: true, data, formData, drawerOpened: Boolean(useDrawer) };
      }

      async onSubmit(evt) {
        evt.preventDefault();
        this.hideError();
        this.els.atc?.classList.add('is-loading');

        try {
          const result = await this.addToCart({ openDrawer: true });
          if (!result.ok) {
            if (result.error === 'unavailable') return;
            this.showError(result.error);
            return;
          }

          if (!result.drawerOpened) {
            window.location = `${window.Shopify?.routes?.root || '/'}cart`;
          }
        } catch (err) {
          this.showError('Unable to add to cart. Please try again.');
          console.error(err);
        } finally {
          this.els.atc?.classList.remove('is-loading');
        }
      }

      async addToCartThenCheckout() {
        this.hideError();
        this.els.atc?.classList.add('is-loading');
        try {
          const result = await this.addToCart({ openDrawer: false, sections: false });
          if (!result.ok) {
            if (result.error !== 'unavailable') this.showError(result.error);
            return;
          }
          window.location = '/checkout';
        } catch (err) {
          this.showError('Unable to checkout. Please try again.');
          console.error(err);
        } finally {
          this.els.atc?.classList.remove('is-loading');
        }
      }

      showError(message) {
        if (!this.els.error) return;
        this.els.error.textContent = message;
        this.els.error.classList.add('is-visible');
      }

      hideError() {
        if (!this.els.error) return;
        this.els.error.textContent = '';
        this.els.error.classList.remove('is-visible');
      }
    }
  );
}
